import type { HttpClient } from "../../../services/deckorateApi";
import type { SpeakerNotesSlideContent } from "../../../types";
import { logError, scrubMessage } from "../../../../../telemetry";

/** connectorSystemName tag for all Deckorate Speaker Notes telemetry. */
const SPEAKER_NOTES_CONNECTOR = "deckorate";

/** Centralized so every catch site in this feature extracts error messages the same way. */
export function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/** Reports a Speaker Notes failure through the shared telemetry pipeline — never `console.*`. */
export function logSpeakerNotesError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "SpeakerNotesError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: SPEAKER_NOTES_CONNECTOR, reason },
  );
}

/**
 * Builds a `.catch` handler that reports an otherwise-unhandled rejection to
 * telemetry. The generate/apply/copy actions this wraps already catch their
 * own errors and surface them via UI state, so this only fires if a future
 * change makes one of them throw before its own try/catch runs — it exists
 * so that case is still reported instead of silently swallowed or logged to
 * the console.
 */
export function reportSpeakerNotesFailure(reason: string): (error: unknown) => void {
  return (error: unknown) => logSpeakerNotesError(reason, getErrorMessage(error, "Unexpected error"));
}

interface SpeakerNotesApiTableShape {
  table_data: string[][];
}

interface SpeakerNotesApiRequest {
  request_id: string;
  slide_content: {
    text_content: string;
    shapes: SpeakerNotesApiTableShape[];
  };
}

interface SpeakerNotesApiResponse {
  request_id: string;
  result: string;
}

/** Prefix for every generated request_id, regardless of which strategy below produced it. */
const REQUEST_ID_PREFIX = "speaker-notes-";

/**
 * Counts calls that fell through to the last-resort id below. Module-scoped
 * (not per-request) purely so consecutive fallback ids stay unique for the
 * lifetime of this page/tab; it has no meaning outside that.
 */
let fallbackRequestSequence = 0;

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * request_id is a non-secret correlation id echoed back for request/response
 * tracing in logs — it is never used for auth or any other security decision.
 * Three strategies are tried in order, for browser compatibility:
 * randomUUID() is preferred; getRandomValues() covers browsers that support
 * Web Crypto but not randomUUID(); and, only when Web Crypto is entirely
 * unavailable, a timestamp + monotonic counter as a last resort. That last
 * fallback is NOT cryptographically secure and must never be used for
 * anything beyond a best-effort, non-secret correlation id — since the id
 * needs no randomness at all, it deliberately avoids Math.random() too.
 */
function newRequestId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  if (cryptoObj?.getRandomValues) {
    return `${REQUEST_ID_PREFIX}${bytesToHex(cryptoObj.getRandomValues(new Uint8Array(16)))}`;
  }
  fallbackRequestSequence += 1;
  return `${REQUEST_ID_PREFIX}${Date.now().toString(36)}-${fallbackRequestSequence}`;
}

/** POST /v1/deckorate/content/speaker-notes — returns the drafted notes text (bulleted, one point per line). */
export async function postSpeakerNotes(
  http: HttpClient,
  slideContent: SpeakerNotesSlideContent,
): Promise<string> {
  const request: SpeakerNotesApiRequest = {
    request_id: newRequestId(),
    slide_content: {
      text_content: slideContent.textContent,
      shapes: slideContent.shapes.map((shape) => ({ table_data: shape.tableData })),
    },
  };
  const response = await http.post<SpeakerNotesApiResponse>("/v1/deckorate/content/speaker-notes", request);
  return response.result;
}
